import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, BellRing, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BookingDialog } from "@/components/BookingDialog";
import { DeadlineCell } from "@/components/DeadlineCell";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { BOOKINGS } from "@/constants/testIds";

const FILTERS = [
  { id: "active", label: "Perlu Tindakan" },
  { id: "open", label: "Belum Selesai" },
  { id: "done", label: "Selesai" },
  { id: "all", label: "Semua" },
];
const isDone = (b) => b.paid && b.guest_done;
const match = { active: (b) => b.reminders.length > 0, open: (b) => !isDone(b), done: isDone, all: () => true };

const Row = ({ b, showOwner, onEdit, onDelete }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={BOOKINGS.row}>
    <td className="px-4 py-3">
      <p className="font-semibold text-slate-900">{b.booking_code}</p>
      <p className="text-sm text-slate-600">{b.name}</p>
      {showOwner && <p className="mt-0.5 text-xs text-slate-400">oleh {b.owner_name}</p>}
      {b.reminders.length > 0 && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"><BellRing className="h-3 w-3" />{b.reminders.length} pengingat</span>}
    </td>
    <td className="px-4 py-3"><DeadlineCell booking={b} kind="payment" field="payment_deadline" done="paid" /></td>
    <td className="px-4 py-3"><DeadlineCell booking={b} kind="guest" field="guest_deadline" done="guest_done" /></td>
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <button onClick={() => onEdit(b)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Revisi" data-testid={BOOKINGS.editButton}><Pencil className="h-4 w-4" /></button>
      <button onClick={() => onDelete(b)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label="Hapus" data-testid={BOOKINGS.deleteButton}><Trash2 className="h-4 w-4" /></button>
    </td>
  </tr>
);

const Card = ({ b, showOwner, onEdit, onDelete }) => (
  <li className="space-y-3 p-4" data-testid={BOOKINGS.card}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-slate-900">{b.booking_code}</p>
        <p className="text-sm text-slate-600">{b.name}</p>
        {showOwner && <p className="text-xs text-slate-400">oleh {b.owner_name}</p>}
        {b.reminders.length > 0 && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"><BellRing className="h-3 w-3" />{b.reminders.length} pengingat</span>}
      </div>
      <div className="flex shrink-0">
        <button onClick={() => onEdit(b)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Revisi" data-testid={BOOKINGS.cardEdit}><Pencil className="h-4 w-4" /></button>
        <button onClick={() => onDelete(b)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label="Hapus" data-testid={BOOKINGS.cardDelete}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3">
      <div><p className="mb-1 text-[10px] uppercase tracking-[0.15em] text-slate-500">Pelunasan</p><DeadlineCell booking={b} kind="payment" field="payment_deadline" done="paid" /></div>
      <div><p className="mb-1 text-[10px] uppercase tracking-[0.15em] text-slate-500">Guest Data</p><DeadlineCell booking={b} kind="guest" field="guest_deadline" done="guest_done" /></div>
    </div>
  </li>
);

export default function Bookings() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user.role);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, booking: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await api.get("/bookings")).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter(match[filter]).filter((b) => !s || [b.name, b.booking_code, b.owner_name].some((x) => x.toLowerCase().includes(s)));
  }, [items, filter, q]);

  const remove = async () => {
    try {
      await api.delete(`/bookings/${toDelete.id}`);
      toast.success("Booking dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <p className="overline-label">Program CS</p>
          <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Reminder Booking</h1>
          <p className="mt-2 text-sm text-slate-500">Pengingat H-2 Timelimit Pelunasan dan H-3 Timelimit Guest Data. {isAdmin ? "Anda melihat semua booking." : "Anda melihat booking milik Anda."}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 hover:text-brand" aria-label="Segarkan" data-testid={BOOKINGS.refresh}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <Button onClick={() => setDialog({ open: true, booking: null })} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid={BOOKINGS.addButton}><Plus className="h-4 w-4 mr-1" /> Tambah Booking</Button>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, kode booking, atau CS..." className="h-11 pl-10 bg-white" data-testid={BOOKINGS.search} />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`${BOOKINGS.filter}-${f.id}`}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === f.id ? "border-brand bg-brand text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{f.label}</button>
          ))}
        </div>
      </div>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white md:hidden">
        {shown.map((b) => <Card key={b.id} b={b} showOwner={isAdmin} onEdit={(x) => setDialog({ open: true, booking: x })} onDelete={setToDelete} />)}
        {shown.length === 0 && <li className="px-4 py-12 text-center text-sm text-slate-500" data-testid={BOOKINGS.cardEmpty}>Tidak ada booking.</li>}
      </ul>
      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white md:block">
        <table className="w-full min-w-[640px] text-left">
          <thead><tr className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
            <th className="px-4 py-3 font-semibold">Booking</th><th className="px-4 py-3 font-semibold">Timelimit Pelunasan</th>
            <th className="px-4 py-3 font-semibold">Timelimit Guest Data</th><th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {shown.map((b) => <Row key={b.id} b={b} showOwner={isAdmin} onEdit={(x) => setDialog({ open: true, booking: x })} onDelete={setToDelete} />)}
            {shown.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500" data-testid={BOOKINGS.empty}>Tidak ada booking.</td></tr>}
          </tbody>
        </table>
      </div>
      <BookingDialog open={dialog.open} booking={dialog.booking} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        title="Hapus booking?" description={`Booking ${toDelete?.booking_code} (${toDelete?.name}) akan dihapus permanen.`} />
    </div>
  );
}
